// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentQuestionBackground — quiet ambient backdrop for the question screen
 * @version 0.2.1
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import styles from './AssessmentQuestionBackground.module.css';

/*
 * AssessmentQuestionBackground — quiet ambient backdrop for the in-flow
 * question screen of the assessment-taking experience.
 *
 *   <AssessmentQuestionBackground />
 *
 * Renders a dark-navy base surface with a single soft radial glow blooming in
 * from the upper-right corner. Unlike the broader assessment backdrop, this
 * variant carries no orbiting decoration — the glow alone, so it stays out of
 * the way of the question content and answer cards that sit above it.
 *
 * Positioning model: the glow is anchored to the viewport's right edge (its
 * box extends past the top-right corner), so the bloom stays pinned to the
 * upper-right across viewport widths. Mounted as an absolute fill behind the
 * screen content (`inset: 0`, `pointer-events: none`).
 */

// Glow block — square; its gradient fades to transparent at the box corners.
const GLOW_SIZE = 1064;
const GLOW_INNER_OPACITY = 0.34;

export interface AssessmentQuestionBackgroundProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Colour treatment. `dark` (default) is the navy base with a screen-blended
   * teal bloom; `light` is the light-teal base with the same bloom composited
   * normally (screen wouldn't read on a light surface). Per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

export const AssessmentQuestionBackground = forwardRef<HTMLDivElement, AssessmentQuestionBackgroundProps>(
  function AssessmentQuestionBackground({ className, theme = 'dark', as, ...rest }, ref) {
  const Component = as ?? 'div';
  const isLight = theme === 'light';
  const rootClass = [styles.root, isLight ? styles.light : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <Component ref={ref} className={rootClass} aria-hidden="true" {...rest}>
      {/* Glow — soft radial gradient anchored to the upper-right corner. */}
      <div
        className={styles.glow}
        style={{ width: GLOW_SIZE, height: GLOW_SIZE }}
      >
        <svg
          width={GLOW_SIZE}
          height={GLOW_SIZE}
          viewBox={`0 0 ${GLOW_SIZE} ${GLOW_SIZE}`}
        >
          <defs>
            {/* r=70.71% so the gradient reaches full transparency exactly at
                the box corners (matching the source's 70.71% ending shape). */}
            {/* Light stops match the info-page background's bloom exactly, so
                the wash reads identically on question and info screens. */}
            <radialGradient id="hd-aqb-glow" cx="50%" cy="50%" r="70.71%">
              <stop offset="0%"                       className={styles.glowStop} stopOpacity={isLight ? 0.2 : GLOW_INNER_OPACITY} />
              <stop offset={isLight ? '70%' : '100%'} className={styles.glowStop} stopOpacity={0} />
            </radialGradient>
          </defs>
          <rect width={GLOW_SIZE} height={GLOW_SIZE} fill="url(#hd-aqb-glow)" />
        </svg>
      </div>
    </Component>
  );
  },
);

AssessmentQuestionBackground.displayName = 'AssessmentQuestionBackground';
